import React, { useEffect, useRef, useState } from 'react';
import { DarkMode } from '@aparajita/capacitor-dark-mode';
import { Box, Divider, Flex, SimpleGrid, Text, useColorMode } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { SHDarkModeSwitchIcon, SHLightModeSwitchIcon } from 'clipsal-cortex-icons/src/custom-icons';

import { SEHomeCard } from '../../../common/components/SEHomeCard';
import { SlidingAnimationPageBase } from '../../../common/components/SlidingAnimationPageBase';
import { getTimeFormat } from './settings-helpers';
import { SettingSwitch } from './SettingSwitch';

export function SettingsDisplay() {
  const { colorMode, setColorMode } = useColorMode();
  const { t } = useTranslation();
  const [shiftColorModeAutomatically, setShiftColorModeAutomatically] = useState(
    localStorage.getItem('shiftColorModeAutomatically') === 'true'
  );
  const [timeFormat, setTimeFormat] = useState<'24HR' | 'AM/PM'>(getTimeFormat());
  const isMouseDown = useRef(false);

  useEffect(() => {
    localStorage.setItem('timeFormat', timeFormat);
  }, [timeFormat]);

  /* istanbul ignore next -- @preserve */
  async function handleToggleSystemColorMode(shiftAutomatically: boolean) {
    setShiftColorModeAutomatically(shiftAutomatically);
    localStorage.setItem('shiftColorModeAutomatically', shiftAutomatically.toString());

    if (shiftAutomatically) {
      const isDarkMode = await DarkMode.isDarkMode();
      if (isDarkMode.dark) {
        setColorMode('dark');
      } else {
        setColorMode('light');
      }
    }

    DarkMode.update();
  }

  function handleMouseDown() {
    isMouseDown.current = true;
  }

  function handleMouseUp() {
    isMouseDown.current = false;
  }

  return (
    <SlidingAnimationPageBase title={t('Account.display')} backURL={`../home?direction=back`}>
      <Box px={5}>
        <Text
          pt={5}
          onTouchStart={handleMouseDown}
          onTouchEnd={handleMouseUp}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {t('Account.appearance').toUpperCase()}
        </Text>
        <SEHomeCard borderTopLeftRadius={'5px'} mb={5}>
          <SimpleGrid opacity={shiftColorModeAutomatically ? 0.3 : 1} px={2} pt={8} columns={2}>
            <Flex
              data-testid="dark-mode-option"
              data-is-enabled={colorMode === 'dark'}
              onClick={() => {
                if (!shiftColorModeAutomatically) setColorMode('dark');
              }}
              direction="column"
              alignItems="center"
            >
              <SHDarkModeSwitchIcon w="100%" h="170px" isEnabled={colorMode === 'dark'} />
              <Text fontWeight={500} mt={1}>
                {t('Account.dark mode')}
              </Text>
            </Flex>
            <Flex
              data-testid="light-mode-option"
              data-is-enabled={colorMode === 'light'}
              onClick={() => {
                if (!shiftColorModeAutomatically) setColorMode('light');
              }}
              direction="column"
              alignItems="center"
            >
              <SHLightModeSwitchIcon w="100%" h="170px" isEnabled={colorMode === 'light'} />
              <Text fontWeight={500} mt={1}>
                {t('Account.light mode')}
              </Text>
            </Flex>
          </SimpleGrid>

          <Divider borderColor={'rgba(151, 151, 151, 0.3)'} my={4} />

          <SettingSwitch
            px={3}
            py={5}
            isChecked={shiftColorModeAutomatically}
            data-testid="system-color-mode-enabled"
            text={t('Account.shift auto')}
            onChange={handleToggleSystemColorMode}
          />

          <Divider borderColor={'rgba(151, 151, 151, 0.3)'} />

          <SettingSwitch
            px={3}
            py={5}
            onChange={(isChecked) => {
              setTimeFormat(isChecked ? '24HR' : 'AM/PM');
            }}
            isChecked={timeFormat == '24HR'}
            data-testid={'time-format-switch'}
            text={t('Account.show time 24hr')}
          />
        </SEHomeCard>
        <Text fontSize="md" mt={2}>
          {t('Account.display mode description')}
        </Text>
      </Box>
    </SlidingAnimationPageBase>
  );
}
